import React, { useState, useEffect } from 'react';
import PublicLayout from '../../components/public/PublicLayout';
import { Stethoscope, FileText, CheckCircle } from 'lucide-react';

const ClinicalGuidelinesPage = () => {
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const [guidelines, setGuidelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const fetchGuidelines = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/resources/clinical-guidelines`);
        const data = await response.json();
        if (data.success) {
          setGuidelines(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching clinical guidelines:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGuidelines();
  }, []);

  // Get unique categories from data
  const categories = ['All', ...new Set(guidelines.map(g => g.category).filter(Boolean))];

  const filteredGuidelines = selectedCategory === 'All' 
    ? guidelines 
    : guidelines.filter(guideline => guideline.category === selectedCategory);

  return (
    <PublicLayout>
      <div className="py-5">
        <div className="container">
          {/* Header */}
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4" 
                   style={{ width: '80px', height: '80px', backgroundColor: '#6B8E23' }}>
                <Stethoscope className="text-white" size={40} />
              </div>
              <h1 className="display-4 fw-bold text-dark mb-3">Clinical Guidelines</h1>
              <p className="lead text-muted">
                Best practices and clinical protocols for respiratory care
              </p>
            </div>
          </div>

          {/* Category Filter */}
          <div className="row justify-content-center mb-4">
            <div className="col-lg-10">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`btn btn-sm ${
                      selectedCategory === category
                        ? 'text-white'
                        : 'btn-outline-secondary'
                    }`}
                    style={selectedCategory === category ? { backgroundColor: '#6B8E23', borderColor: '#6B8E23' } : {}}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Guidelines Grid */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-3">Loading clinical guidelines...</p>
                </div>
              ) : guidelines.length === 0 ? (
                <div className="alert alert-info text-center">
                  <h5>No clinical guidelines available yet</h5>
                  <p className="mb-0">Clinical guidelines are currently being developed by our expert committees.</p>
                </div>
              ) : (
                <>
                  <div className="row g-4 mb-5">
                    {filteredGuidelines.map((guideline, index) => (
                      <div key={index} className="col-md-6">
                        <div className="card h-100 shadow-sm border-0">
                          <div className="card-body p-4">
                            <div className="d-flex align-items-start mb-3">
                              <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0" 
                                   style={{ width: '48px', height: '48px', backgroundColor: '#e8f5e9' }}>
                                <FileText style={{ color: '#6B8E23' }} size={24} />
                              </div>
                              <div className="flex-grow-1">
                                <h5 className="fw-bold text-dark mb-2">{guideline.title}</h5>
                                <p className="text-muted small mb-3">{guideline.description}</p>
                                
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  <span className="badge text-white" style={{ backgroundColor: '#6B8E23' }}>
                                    {guideline.category}
                                  </span>
                                  <span className="small text-muted">v{guideline.version}</span>
                                  <span className={`badge ${guideline.status === 'Available' ? 'bg-success' : 'bg-warning'} text-dark`}>
                                    {guideline.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Features Section */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-5">
                  <h3 className="h4 fw-bold text-dark mb-4">Our Clinical Guidelines</h3>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-microscope me-2" style={{ color: '#6B8E23' }}></i>
                        Evidence-Based
                      </h5>
                      <p className="text-muted">
                        All guidelines are developed using the latest research evidence and international best practices.
                      </p>
                    </div>
                    <div className="col-md-4">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-globe-africa me-2" style={{ color: '#6B8E23' }}></i>
                        Context-Appropriate
                      </h5>
                      <p className="text-muted">
                        Adapted for African healthcare settings, considering resource availability and local contexts.
                      </p>
                    </div>
                    <div className="col-md-4">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-sync-alt me-2" style={{ color: '#6B8E23' }}></i>
                        Regularly Updated
                      </h5>
                      <p className="text-muted">
                        Guidelines are reviewed and updated regularly to reflect emerging evidence and practices.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Development Process */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-5">
                  <h3 className="h4 fw-bold text-dark mb-4">Guideline Development Process</h3>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="d-flex align-items-start">
                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0"
                             style={{ width: '40px', height: '40px', backgroundColor: '#e8f5e9' }}>
                          <span className="fw-bold" style={{ color: '#6B8E23' }}>1</span>
                        </div>
                        <div>
                          <h6 className="fw-bold text-dark mb-1">Expert Committee Review</h6>
                          <p className="text-muted small mb-0">Clinical experts review evidence and draft initial guidelines</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-start">
                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0"
                             style={{ width: '40px', height: '40px', backgroundColor: '#e8f5e9' }}>
                          <span className="fw-bold" style={{ color: '#6B8E23' }}>2</span>
                        </div>
                        <div>
                          <h6 className="fw-bold text-dark mb-1">Stakeholder Consultation</h6>
                          <p className="text-muted small mb-0">Members and healthcare institutions provide feedback</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-start">
                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0"
                             style={{ width: '40px', height: '40px', backgroundColor: '#e8f5e9' }}>
                          <span className="fw-bold" style={{ color: '#6B8E23' }}>3</span>
                        </div>
                        <div>
                          <h6 className="fw-bold text-dark mb-1">Pilot Testing</h6>
                          <p className="text-muted small mb-0">Guidelines tested in real-world clinical settings</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-start">
                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0"
                             style={{ width: '40px', height: '40px', backgroundColor: '#e8f5e9' }}>
                          <span className="fw-bold" style={{ color: '#6B8E23' }}>4</span>
                        </div>
                        <div>
                          <h6 className="fw-bold text-dark mb-1">Final Publication</h6>
                          <p className="text-muted small mb-0">Approved guidelines made available to members</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="card shadow border-0" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
                <div className="card-body p-5 text-center text-white">
                  <h3 className="h4 fw-bold mb-3">Clinical Guidelines in Development</h3>
                  <p className="mb-4">
                    Our expert committees are finalizing comprehensive clinical guidelines. 
                    ARCA members will receive priority access and implementation support.
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-3">
                    <a href="/get-involved/become-member" className="btn btn-light btn-lg fw-bold">Become a Member</a>
                    <a href="/contact" className="btn btn-outline-light btn-lg">Request Updates</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ClinicalGuidelinesPage;
